<template>
  <div
    class="tw-flex tw-flex-col tw-bg-white tw-bg-lighthouse-light tw-bg-cover tw-h-screen tw-w-full tw-bg-fixed tw-bg-center"
  >
    <t-auth-header />
    <section
      class="tw-mb-auto tw-items-center tw-self-center tw-rounded tw-p-9 tw-bg-white tw-bg-opacity-85"
    >
      <cl-heading class="tw-text-center">
        {{ $t("password-reset-title") }}
      </cl-heading>
      <form class="tw-w-full tw-max-w-md" @submit.prevent>
        <cl-form-group class="tw-mb-6">
          <cl-p class="tw-text-center">
            {{ $t("password-reset-description") }}
          </cl-p>
        </cl-form-group>
        <cl-form-group class="tw-mb-6 tw-mt-4">
          <cl-form-input
            id="email"
            name="email"
            v-model:value="v$.email.$model as unknown as string"
            :placeholder="$t('sign-in-email-placeholder')"
            :size="'large'"
            :state="!v$.email.$dirty ? null : !v$.email.$error"
          />
          <span
            v-if="v$.email.$error"
            class="tw-text-base tw-font-normal tw-text-system-error-500"
            >{{ $t("valid-email-format") }}</span
          >
        </cl-form-group>
        <cl-form-group class="tw-mb-6 tw-items-center">
          <div class="tw-w-[70%]">
            <cl-button
              :variant="'secondary'"
              :size="'large'"
              :type="'submit'"
              :disabled="fetching"
              fullwidth
              data-test-id="valid-email-continue-btn"
              @on-click="submit"
            >
              <cl-spinner
                :loading-text="$t('loading')"
                v-if="fetching"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ $t("continue") }}</span>
            </cl-button>
          </div>
        </cl-form-group>
        <cl-form-group class="tw-mb-6">
          <cl-p class="tw-text-center">
            {{ $t("sign-in-return-to") }}
            <router-link
              v-slot="{ href, navigate }"
              :to="{ name: 'auth-sign-in' }"
            >
              <a
                :href="href"
                class="tw-text-primary-grey-700"
                @click="navigate"
                >{{ $t("sign-in-text-block") }}</a
              >
            </router-link>
          </cl-p>
        </cl-form-group>
      </form>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email as emailValidator } from "@vuelidate/validators";
import { useI18n } from "vue-i18n";
import { usePasswordResetStore } from "@/store/passwordReset.store";
import { useToastStore } from "@/components/TToasts/toast.store";
import TAuthHeader from "@/components/TAuthHeader/index.vue";

type PasswordResetRequest = {
  email: string;
  domain: string;
};

type PasswordResetResponse = {
  data: {
    message: string;
  };
};

const { t } = useI18n();
const toastStore = useToastStore();
const passwordResetStore = usePasswordResetStore();

const fetching = ref<boolean>(false);
const email = ref<string>("");

const rules = {
  email: { required, email: emailValidator },
};

const v$ = useVuelidate(rules, { email });

const requestResetLink = async (
  payload: PasswordResetRequest,
): Promise<PasswordResetResponse> => {
  return (await passwordResetStore.requestResetLink(
    payload,
  )) as PasswordResetResponse;
};

const submit = async (): Promise<void> => {
  const isValid = await v$.value.$validate();

  if (!isValid) {
    return;
  }

  try {
    fetching.value = true;

    const res = await requestResetLink({
      email: email.value,
      domain: location.origin,
    });

    toastStore.displayToast({
      title: t("success"),
      message: res.data.message,
      duration: 10000,
      variant: "success",
      testId: "reset-password-toast-success",
    });
  } catch {
    // Handled in axios interceptor
  } finally {
    fetching.value = false;
  }
};
</script>
